import React, { ReactNode } from 'react';

import { Close } from '@mui/icons-material';
import { IconButton, capitalize } from '@mui/material';

import { useAppSelector } from 'state/hooks';
import {
  selectComparison,
  selectCurrentProjectInfo,
  selectShortestAvailableBin,
  selectTargetDate,
} from 'state/workflowSlice';

import { formatComparisonMonthName } from 'features/chart/nrtChartCommon';
import { formatTimeOfDay12HrClock } from 'features/common/datetimeUtils';
import {
  formatDistance,
  formatSpeedKmph,
  formatTraveltime,
} from 'features/common/utils';
import {
  RouteQueueSegment,
  RouteQueueTagma,
  get_congestion,
  get_minute_offset,
  get_offset_end,
  get_offset_start,
  get_queue_id,
  get_timestamp,
} from 'features/workflow_queuing/common';
import { NA_String } from 'features/workflow_timeline/timelineTooltipContents';

import style from 'features/workflow_queuing/WorkflowQueuing.module.css';

export type TooltipData = {
  tagma: RouteQueueTagma;
};

export function colorForQueueState(tagma: RouteQueueTagma) {
  const state = capitalize(tagma.properties.state.toLowerCase());
  const state_color = {
    Ok: '#306b35',
    Congested: '#E99047',
    Queued: '#DF5E33',
  }[state];
  return state_color;
}

function SegmentStateTag({ tagma }: { tagma: RouteQueueTagma }) {
  const state = capitalize(tagma.properties.state.toLowerCase());
  if (state === 'Ok') return null;
  const state_color = colorForQueueState(tagma);
  return (
    <span
      className={style.segment_state_tag}
      style={{ backgroundColor: `${state_color}55` }}
    >
      <span style={{ color: state_color, marginRight: '0.2rem' }}>●</span>
      {state}
    </span>
  );
}

export function get_total_info(tagmas: RouteQueueTagma[]) {
  let len = 0;
  let delay = 0;
  let typical_tt = 0;
  let queued_len = 0;
  for (let i = 0; i < tagmas.length; i++) {
    const tigma = tagmas[i];
    const tig_len = get_offset_end(tigma) - get_offset_start(tigma);
    len += tig_len;
    if (tigma.properties.state !== 'OK') {
      queued_len += tig_len;
    }
    delay +=
      (tigma.properties as any).atypical_delay_min ||
      (tigma.properties as any).agg_atypical_delay_min ||
      0;
    typical_tt +=
      (tigma.properties as any).typical_traveltime_min ||
      (tigma.properties as any).agg_typical_traveltime_min ||
      0;
  }
  return {
    total_length_km: len,
    queued_length_km: queued_len,
    total_delay_min: delay,
    typical_traveltime_min: typical_tt,
  };
}

export function formatPercentage(prop: number) {
  return `${(prop * 100).toFixed(0)} %`;
}

export function QueueChartTooltipContents({
  tooltipData,
  queues,
  timestamps,
  hoverOffset,
  onCloseClick = undefined,
}: {
  tooltipData: TooltipData;
  queues: { [key: number]: RouteQueueTagma };
  timestamps: { [key: string]: RouteQueueTagma };
  hoverOffset: undefined | number;
  onCloseClick?: () => void;
}) {
  const userProject = useAppSelector(selectCurrentProjectInfo);
  const targetDate = useAppSelector(selectTargetDate);
  const comparison = useAppSelector(selectComparison);
  const binSize = useAppSelector(selectShortestAvailableBin);

  const get_total_queue_info = (queue_id) => {
    const queue: any = queues[queue_id] || [];
    return get_total_info(queue);
  };

  const get_total_timestamp_info = (ts) => {
    return get_total_info(timestamps[ts] || ([] as any));
  };

  const speed = (tooltipData.tagma.properties as any).average_speed_kmph;
  const formattedSpeed =
    speed != null ? formatSpeedKmph(speed, userProject.uses_metric) : NA_String;

  const congestion = get_congestion(tooltipData.tagma as RouteQueueSegment);
  const formattedCongestion =
    congestion !== null ? formatPercentage(1 - congestion) : NA_String;
  const total_timestamp_info = get_total_timestamp_info(
    get_timestamp(tooltipData.tagma),
  );

  const total_queue_info = get_total_queue_info(
    get_queue_id(tooltipData.tagma),
  );

  const formatTravelTimeChange = (v: number) => {
    const fmtd = formatTraveltime(v);
    if (v > 0) {
      return `+ ${fmtd}`;
    }
    return fmtd;
  };

  const row = (label, value): ReactNode => {
    return (
      <div className={style.chart_tooltip_data_row}>
        <span>{label}</span>
        <span>, </span>
        <span>{value}</span>
      </div>
    );
  };

  const segmentInfoChunk = (
    <>
      {row('Speed', formattedSpeed)}
      {row('Congestion', formattedCongestion)}
    </>
  );

  const queueInfoBlock = get_queue_id(tooltipData.tagma) ? (
    <>
      <div className={style.chart_tooltip_block}>
        <h1>Queue</h1>
        {row(
          'Length',
          formatDistance(
            total_queue_info.total_length_km * 1000,
            userProject.uses_metric,
          ),
        )}
        {row(
          'Delay',
          formatTravelTimeChange(total_queue_info.total_delay_min * 60),
        )}
      </div>
      <hr />
    </>
  ) : null;

  const routeInfoBlock = (
    <>
      {row(
        'Queued length',
        formatDistance(
          total_timestamp_info.queued_length_km * 1000,
          userProject.uses_metric,
        ),
      )}
      {row(
        'Queued',
        formatPercentage(
          total_timestamp_info.queued_length_km /
            total_timestamp_info.total_length_km,
        ),
      )}
      {row(
        formatComparisonMonthName(
          comparison,
          targetDate,
          userProject.uses_middle_east_day_types,
        ),
        formatTraveltime(total_timestamp_info.typical_traveltime_min * 60),
      )}
      {row(
        'Delay',
        formatTravelTimeChange(total_timestamp_info.total_delay_min * 60),
      )}
    </>
  );

  const hasSegment = !!hoverOffset;
  return (
    <div className={style.chart_tooltip_container}>
      <div className={style.chart_tooltip_block}>
        <div className={style.chart_tooltip_title_row}>
          <h1>{hasSegment ? 'Segment' : 'Route'}</h1>
          <span style={{ flexGrow: 1 }} />
          <SegmentStateTag tagma={tooltipData.tagma} />
          {onCloseClick && (
            <IconButton onClick={(ev) => onCloseClick()}>
              <Close />
            </IconButton>
          )}
        </div>
        {hasSegment ? segmentInfoChunk : routeInfoBlock}
      </div>
      <hr />
      {queueInfoBlock}
      {hasSegment ? (
        <div className={style.chart_tooltip_block}>
          <h1>Route</h1>
          {routeInfoBlock}
        </div>
      ) : null}
      <div className={style.chart_tooltip_block}>
        <div className={style.chart_tooltip_time}>
          {formatTimeOfDay12HrClock(
            get_minute_offset(tooltipData.tagma, targetDate),
          )}
          {' to '}
          {formatTimeOfDay12HrClock(
            get_minute_offset(tooltipData.tagma, targetDate) + binSize,
          )}
        </div>
      </div>
    </div>
  );
}
